<template>
  <div class="container py-10 mx-auto px-1 sm:px-0">
    <h1 class="text-3xl sm:text-4xl font-bold">Terms & Conditions</h1>

    <div class="mt-4 space-y-4 text-gray-800 leading-relaxed">

      <p><strong>Last Updated: September 29, 2025</strong></p>

      <p>
        These Terms and Conditions govern the relationship between you, the users of our website, 
        and us, (Perfect Storm). By browsing our site and/or using any of the features of our site, 
        you agree to be bound by these Terms and Conditions. These Terms and Conditions are not put 
        in place to be burdensome or difficult; rather, these Terms and Conditions are in place to 
        protect both ourselves and our legitimate customers. If any of our Terms and Conditions are 
        causing issues for you, please contact us at support@(PerfectStorm).net to discuss so 
        that we might seek to find a mutually beneficial alternative or work-around.
      </p>

      <h2 class="text-xl font-semibold mt-6">Section I. General Information and Use</h2>
      <p>
        The information provided on our site is for your general information and use only. It is 
        entirely your responsibility to ensure that any products, services or information available 
        through our website meet your specific needs and requirements.
      </p>

      <h2 class="text-xl font-semibold mt-6">Section II. Shipping Cost, Risk & Liability</h2>
      <p>
        The cost to send shipments to us will be fully paid by the customer. We are not liable for 
        any shipments from you that are lost or damaged on its way to us. We very strongly recommend 
        insurance when shipping any items of value. The condition of items as received will be the 
        basis for your Payout; please refer to our 
        <a href="/how-to-pack-ship" class="text-blue-600 underline">shipping guide</a> 
        for advice on how to protect your cards in transit.
      </p>

      <h2 class="text-xl font-semibold mt-6">Section III. Customer Service</h2>
      <p>
        We strive to provide excellent and transparent customer service for every transaction. 
        Inevitably, small issues will arise and will be handled as best as we are able. We ask that 
        you have patience with us during busy times, and that you give us the opportunity to fix any 
        issues that may occur. Please never hesitate to reach out to us with any questions or concerns 
        you have related to your (Perfect Storm/Storm Count) experience.
      </p>

      <h2 class="text-xl font-semibold mt-6">Section IV. Returns and Disputes</h2>
      <p>
        Under normal circumstances, your shipment processing is considered "finalized" when we send 
        your results. Once finalized, cards cannot be returned to you and our assessed grading/value 
        cannot be disputed. This is because our business relies on specific handling logistics to 
        effectively sort cards and organize them for shipment to our vendors. Once processing is 
        finalized, the cards go into our general outgoing inventory and it is not possible to determine 
        which cards came from your shipment at that point.
      </p>

      <p>
        We realize some customers would like an opportunity to review the processing results (and 
        grading). We offer a "hold for review" option for shipments meeting the following criteria:
      </p>
      <ul class="list-disc ml-6">
        <li>The Shipment is using our Curated or Sorted Service.</li>
        <li>The Shipment is max 500 cards. If you have more than 500 cards to send, please break them into separate Shipments of maximum 500 cards each.</li>
      </ul>

      <p>
        For shipments that have been held for review, there will be <strong>48 hours</strong> from the 
        time our report is issued for the customer to review the results and inform us of any issues or 
        concerns. During that 48 hour window, we will hold the contents of your shipment to make it 
        possible for us to review and resolve your concerns, prior to merging the cards into our general 
        inventory. In the event there are issues or concerns, we will work with you to find a mutually 
        acceptable solution to address it. In the event there are no issues or concerns, the processing 
        will then be finalized.
      </p>

      <h2 class="text-xl font-semibold mt-6">Section V. Payout Methods</h2>
      <p>
        Currently our Payout Methods are via Paypal, mailed Check, Zelle, and ACH. Payouts are generally 
        processed no later than 3 business days after your shipment has been fully processed.
      </p>

      <h3 class="font-semibold">Checks</h3>
      <ul class="list-disc ml-6">
        <li>Checks are mailed via USPS.</li>
        <li>Delivery typically takes 7 business days.</li>
        <li>Checks not cashed within 180 days will be cancelled.</li>
      </ul>

      <h3 class="font-semibold">Paypal</h3>
      <ul class="list-disc ml-6">
        <li>Paypal payments are made the same day your payout has processed.</li>
        <li>Paypal charges fees to you to receive payment.</li>
        <li>Additional holds may apply depending on your account history.</li>
      </ul>

      <h3 class="font-semibold">Zelle</h3>
      <ul class="list-disc ml-6">
        <li>Zelle payments require your bank to support Zelle.</li>
        <li>Zelle payments are made the same day your payout has processed.</li>
        <li>There are daily send limits for Zelle; shipments of $3,000+ require ACH instead.</li>
      </ul>

      <h3 class="font-semibold">ACH / Direct Deposit</h3>
      <ul class="list-disc ml-6">
        <li>ACH requires a U.S.-based bank account.</li>
        <li>ACH info will be collected via Jotform link.</li>
        <li>ACH payments are made the same day your payout has processed.</li>
        <li>No fees to receive an ACH payment.</li>
      </ul>

      <h2 class="text-xl font-semibold mt-6">Section VI. Process of Shipments and Expected Timelines</h2>
      <p>
        We offer three shipment types: Curated, Standard, and Sorted. Each has different fee structures 
        and requirements. Once your shipment is received, we will mark it as "received" and you will be 
        notified via email.
      </p>

      <h3 class="font-semibold">Quantity Limit</h3>
      <p>
        Standard and Curated Shipments: max 2,500 cards (approx. 10 lbs). Sorted Shipments: max 500 cards.
      </p>

      <h3 class="font-semibold">Processing Shipments</h3>
      <ul class="list-disc ml-6">
        <li>Sorted shipments processed in 4 business days, Curated in 5, Standard in 7.</li>
        <li>All cards individually cataloged.</li>
        <li>Grading is final; cannot be disputed after processing.</li>
      </ul>

      <h3 class="font-semibold">Counterfeit/Fake Cards</h3>
      <p>
        Counterfeits assessed at zero value. They may be returned for a $10 fee or destroyed. 
        Excessive counterfeits may result in refusal of future shipments.
      </p>

      <h3 class="font-semibold">Non-English Cards</h3>
      <p>
        Non-English cards are not accepted unless specific exceptions apply (e.g., Strixhaven Mystical Archive).
      </p>

      <h2 class="text-xl font-semibold mt-6">Section VII. Condition Guide</h2>
      <p>
        Our grading ranges are NM (Near Mint), LP (Lightly Played), MP (Moderately Played), HP (Heavily Played), 
        and DMG (Damaged). Borderline cards will be considered the lesser grade. Grading follows industry standards 
        and our buylist partners’ guidelines.
      </p>

    </div>
  </div>
</template>

<script setup>
// no script needed yet
</script>
